import React from 'react';
import { Star, StarBorder } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

interface RatingDisplayProps {
  rating: number;
  reviewCount: number;
  size?: 'small' | 'medium' | 'large';
  showReviewCount?: boolean;
  maxRating?: number;
}

const RatingDisplay: React.FC<RatingDisplayProps> = ({
  rating,
  reviewCount,
  size = 'medium',
  showReviewCount = true,
  maxRating = 5
}) => {
  const getStarSize = () => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 24;
      default:
        return 20;
    }
  };

  const getFontSize = () => {
    switch (size) {
      case 'small':
        return '0.75rem';
      case 'large':
        return '1.1rem';
      default:
        return '0.875rem';
    }
  };

  const renderStars = () => {
    const stars = [];
    const starSize = getStarSize();
    
    for (let i = 1; i <= maxRating; i++) {
      if (i <= Math.floor(rating)) {
        // Full star
        stars.push(
          <Star
            key={i}
            sx={{
              fontSize: starSize,
              color: '#ffc107',
            }}
          />
        );
      } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
        // Half star (approximated as full star with reduced opacity)
        stars.push(
          <Star
            key={i}
            sx={{
              fontSize: starSize,
              color: '#ffc107',
              opacity: 0.5,
            }}
          />
        );
      } else {
        // Empty star
        stars.push(
          <StarBorder
            key={i}
            sx={{
              fontSize: starSize,
              color: '#e0e0e0',
            }}
          />
        );
      }
    }
    
    return stars;
  };

  const formatReviewCount = (count: number): string => {
    if (count === 0) {
      return 'No reviews';
    } else if (count === 1) {
      return '1 review';
    } else {
      return `${count} reviews`;
    }
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Box display="flex" alignItems="center">
        {renderStars()}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: getFontSize(),
          color: 'text.secondary',
          ml: 0.5,
        }}
      >
        {rating.toFixed(1)}
      </Typography>
      {showReviewCount && (
        <Typography
          variant="body2"
          sx={{
            fontSize: getFontSize(),
            color: 'text.secondary',
            ml: 0.5,
          }}
        >
          ({formatReviewCount(reviewCount)})
        </Typography>
      )}
    </Box>
  );
};

export default RatingDisplay;
